import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentDots,
  faFilePdf,
  faMap,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import './ResultList.scss';
import PaginationComponent from '../Pagination/Pagination';
import ResultListSkeleton from '../ResultListSkeleton/ResultListSkeleton';
import FilterPills from '../FilterPills/FilterPills';
// Use VoyagerSearchContext for Voyager API integration
import { useVoyagerSearch as useSearch } from '../../context/VoyagerSearchContext';
import { Metadata } from '../../models/search-result';
import {
  publishOpenChatModal,
  subscribeChatButtonConfig,
} from '../../../VoyagerEnhancedSearch/utils/eventBus';
import { getDetailsLink } from './ResultList.helpers';
import { buildAgolUrl, isAgolEligibleFormat } from '../../constants/agolConfig';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

// Icon constants
const iconFaFilePdf = faFilePdf;

// Determine badge indicator color based on badge text content
const getBadgeIndicatorColor = (badgeText: string): string => {
  const text = badgeText.toLowerCase();

  // Orange/warning indicators
  if (
    text.includes('fire') ||
    text.includes('damage') ||
    text.includes('detected') ||
    text.includes('warning') ||
    text.includes('alert') ||
    text.includes('hazard')
  ) {
    return 'orange';
  }

  // Red/critical indicators
  if (
    text.includes('critical') ||
    text.includes('severe') ||
    text.includes('emergency') ||
    text.includes('destroyed') ||
    text.includes('high risk')
  ) {
    return 'red';
  }

  // Blue indicators
  if (
    text.includes('water') ||
    text.includes('flood') ||
    text.includes('storm') ||
    text.includes('track') ||
    text.includes('hurricane') ||
    text.includes('idalia')
  ) {
    return 'blue';
  }

  // Green/positive indicators (default)
  if (
    text.includes('undamaged') ||
    text.includes('safe') ||
    text.includes('clear') ||
    text.includes('detection') ||
    text.includes('monitoring')
  ) {
    return 'green';
  }

  // Default to green
  return 'green';
};

const getChatDocumentUrl = (metadata: Metadata): string => {
  const linkTypes = metadata.linkTypes || [];
  const pdfLink = linkTypes.find((link) => {
    const href = link.href || '';
    const fileType = link.fileType || '';
    return (
      href.toLowerCase().includes('.pdf') ||
      fileType.toLowerCase().includes('pdf')
    );
  });

  return (
    pdfLink?.href ||
    metadata.link ||
    (metadata as any).download ||
    (metadata as any).fullpath ||
    ''
  );
};

const hasPdfFormat = (metadata: Metadata): boolean => {
  const values = [
    metadata.standardName,
    metadata.type,
    (metadata as any).format,
    (metadata as any).format_type,
    ...((metadata.linkTypes || []).map((link) => link.fileType) || []),
  ];

  return values.some((value) =>
    String(value || '')
      .toLowerCase()
      .includes('pdf'),
  );
};

const getPdfPageCount = (metadata: Metadata): number | null => {
  const candidateFields = [
    (metadata as any).pages,
    (metadata as any).pageCount,
    (metadata as any).page_count,
    (metadata as any).page_count_i,
    (metadata as any).numPages,
    (metadata as any).num_pages,
    (metadata as any).pdfPages,
    (metadata as any).pdf_pages,
    (metadata as any).pdf_page_count,
  ];

  const pageCount = candidateFields
    .map((value) => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const match = value.match(/\d+/);
        return match ? Number(match[0]) : Number.NaN;
      }
      return Number.NaN;
    })
    .find((value) => Number.isInteger(value) && value > 0);

  return pageCount || null;
};

// View type options
type ViewType = 'map' | 'card' | 'table' | 'grid' | 'summary';

// Interface for card display field configuration
interface CardDisplayField {
  field: string;
  label: string;
}

interface ResultListProps {
  stateKey?: string;
  viewType?: ViewType;
  detailsPagePath?: string;
  cardDisplayFields?: CardDisplayField[];
  cardBadgeField?: string;
  cardColumns?: number;
  agolEligibleFormats?: string[];
}

const ResultList: React.FC<ResultListProps> = ({
  stateKey = 'default',
  viewType = 'card',
  detailsPagePath = '/voyager-details',
  cardDisplayFields = [],
  cardBadgeField = '',
  cardColumns = 4,
  agolEligibleFormats = [],
}) => {
  // eslint-disable-next-line no-console
  console.log(
    '[ResultList-VoyagerSearchPageBlock] Component rendered/mounted with stateKey:',
    stateKey,
  );

  // Use SearchContext
  const { searchState, applySearchParams, setHoveredItem, clearHoveredItem } =
    useSearch();
  const [chatWithPdfButtonText, setChatWithPdfButtonText] = useState<
    string | null
  >(null);
  const { arcgisUrl: agolBaseUrl } = useSearch();

  useEffect(
    () =>
      subscribeChatButtonConfig((config) => {
        setChatWithPdfButtonText(config?.chatWithPdfButtonText || null);
      }),
    [],
  );

  // Add event listener for solr-query-url from the Voyager chat block
  useEffect(() => {
    const handler = (e: Event) => {
      // eslint-disable-next-line no-console
      console.log(
        '[ResultList-VoyagerSearchPageBlock] Received solr-query-url event:',
        e,
      );
      const detail = (e as CustomEvent)?.detail as any;
      // eslint-disable-next-line no-console
      console.log('[ResultList-VoyagerSearchPageBlock] Event detail:', detail);
      const url =
        detail?.url ??
        (detail && typeof detail === 'string' ? detail : undefined);
      // eslint-disable-next-line no-console
      console.log('[ResultList-VoyagerSearchPageBlock] Extracted URL:', url);
      if (url) {
        // eslint-disable-next-line no-console
        console.log(
          '[ResultList-VoyagerSearchPageBlock] Triggering fetch from Solr URL:',
          url,
        );
        // TODO: fetch from URL and update results
      }
    };

    window.addEventListener('solr-query-url', handler as EventListener);
    // eslint-disable-next-line no-console
    console.log(
      '[ResultList-VoyagerSearchPageBlock] Subscribed to solr-query-url event',
    );

    return () => {
      window.removeEventListener('solr-query-url', handler as EventListener);
      // eslint-disable-next-line no-console
      console.log(
        '[ResultList-VoyagerSearchPageBlock] Unsubscribed from solr-query-url event',
      );
    };
  }, []);

  // Use context state
  const currentState = searchState;
  const handlePageChange = (newPageIndex: number) => {
    const pageIndex = Number.isNaN(newPageIndex) ? 0 : newPageIndex;
    applySearchParams({ pageIndex });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const pageSize = Number.isNaN(newPageSize) ? 10 : newPageSize;
    applySearchParams({ pageIndex: 0, pageSize });
  };

  const handleMetadataClick = (uuid: string) => {
    window.location.href = getDetailsLink(detailsPagePath, uuid);
  };

  const handleChatWithPdfClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    metadata: Metadata,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    publishOpenChatModal({
      documentTitle: metadata.title,
      documentUrl: getChatDocumentUrl(metadata),
      source: 'voyager-search-card',
      uuid: metadata['geonet:info']?.uuid || (metadata as any).id || '',
    });
  };

  const hasResults =
    !currentState?.isLoading &&
    currentState?.results?.data &&
    currentState.results.data.length > 0 &&
    currentState.results.count > 0;

  const showNoResults =
    !currentState?.isLoading &&
    currentState?.results &&
    (currentState.results.count === 0 || !currentState.results.data?.length);

  return (
    <div className="results-list-component-container">
      {showNoResults ? (
        <div className="no-results-container">
          <h3 className="no-results-title">
            Sorry, your search found no results.
          </h3>
          <p className="no-results-subtitle">
            Try broadening your search results:
          </p>
          <ul className="no-results-suggestions">
            <li>Removing filters</li>
            <li>Expanding your search</li>
            <li>Modifying or removing keywords</li>
            <li>Checking the spelling of keywords</li>
          </ul>
          <button
            className="no-results-see-all-btn"
            onClick={() => {
              applySearchParams(
                {
                  search: '',
                  filters: [],
                  bbox: [],
                  pageIndex: 0,
                  place: undefined,
                  locationQuery: '',
                },
                { historyMode: 'replace' },
              );
            }}
          >
            <FontAwesomeIcon icon={faSearch} /> See all results
          </button>
        </div>
      ) : (
        <>
          {((currentState?.results?.count && currentState.results.count > 0) ||
            currentState?.isLoading) && (
            <div className="results-title-controls-container">
              {currentState?.results?.vector && (
                <div className="beta-gpt-search-container">
                  <h3>BETA GPT Search Feature</h3>
                  <p>
                    We have found results that closely match your query, based
                    on your question or similar content.
                  </p>
                </div>
              )}

              <div className="-controls-container">
                <div className="-pagination-container">
                  <PaginationComponent
                    resultCount={currentState.results?.count || 0}
                    pageSize={currentState.query?.pageSize || 25}
                    pageIndex={currentState.query?.pageIndex || 0}
                    pageSizeOptions={[25, 50, 75, 100]}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </div>
              </div>

              <div className="selected-filters-container"></div>
            </div>
          )}

          {/* Add FilterPills after the results header */}
          <FilterPills />

          {currentState?.isLoading && (
            <ResultListSkeleton cardColumns={cardColumns} />
          )}

          {hasResults && (
            <>
              {/* Map View */}
              {viewType === 'map' && (
                <div className="results-map-view">
                  <div className="map-view-placeholder">
                    <FontAwesomeIcon
                      icon={faMap}
                      className="map-placeholder-icon"
                    />
                    <p>Map view shows results on an interactive map.</p>
                    <p className="map-hint">
                      Use the map toggle button above to view results
                      geographically.
                    </p>
                  </div>
                </div>
              )}

              {/* Card View - Featured Block style */}
              {viewType === 'card' && (
                <div
                  className={`results-card-grid view-card results-card-grid--cols-${cardColumns}`}
                >
                  {(currentState.results.data as Metadata[]).map(
                    (metadata: Metadata) => {
                      const pdfPageCount = getPdfPageCount(metadata);
                      const showChatWithPdfButton =
                        Boolean(chatWithPdfButtonText) &&
                        hasPdfFormat(metadata);

                      return (
                        <article
                          key={
                            metadata['geonet:info']?.uuid ||
                            (metadata as any).id
                          }
                          className="featured-card"
                          onMouseEnter={() => setHoveredItem(metadata)}
                          onMouseLeave={() => clearHoveredItem()}
                        >
                          {/* Thumbnail */}
                          <Link
                            to={getDetailsLink(
                              detailsPagePath,
                              metadata['geonet:info']?.uuid ||
                                (metadata as any).id ||
                                '',
                            )}
                            className="featured-card__thumbnail"
                          >
                            {/* Badge overlay - configurable field or fallback to status_text/event */}
                            {(() => {
                              const badgeValue = cardBadgeField
                                ? (metadata as any)[cardBadgeField]
                                : metadata.status_text || metadata.event;
                              return badgeValue ? (
                                <div className="featured-card__badge">
                                  <span
                                    className={`featured-card__badge-indicator featured-card__badge-indicator--${getBadgeIndicatorColor(String(badgeValue))}`}
                                  />
                                  <span className="featured-card__badge-text">
                                    {String(badgeValue)}
                                  </span>
                                </div>
                              ) : null;
                            })()}
                            {metadata.thumbnailUrl ||
                            (metadata as any).thumb ? (
                              <img
                                src={
                                  metadata.thumbnailUrl ||
                                  (metadata as any).thumb
                                }
                                alt={metadata.title || (metadata as any).id}
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="featured-card__placeholder">
                                <FontAwesomeIcon icon={iconFaFilePdf} />
                              </div>
                            )}
                          </Link>

                          <hr className="featured-card__divider" />

                          {/* Content */}
                          <div className="featured-card__content">
                            {metadata.title && (
                              <h3 className="featured-card__title">
                                <Link
                                  to={getDetailsLink(
                                    detailsPagePath,
                                    metadata['geonet:info']?.uuid ||
                                      (metadata as any).id ||
                                      '',
                                  )}
                                >
                                  {metadata.title}
                                </Link>
                              </h3>
                            )}

                            {/* Metadata */}
                            <div className="featured-card__meta">
                              {/* Keywords/Tags display like featured block */}
                              {metadata.keywords &&
                                metadata.keywords.length > 0 && (
                                  <p className="featured-card__meta-item">
                                    <span className="featured-card__meta-label">
                                      Tags:
                                    </span>{' '}
                                    <span className="featured-card__meta-value">
                                      {metadata.keywords.slice(0, 8).join(', ')}
                                    </span>
                                  </p>
                                )}
                              {/* Configurable fields */}
                              {cardDisplayFields.length > 0 ? (
                                cardDisplayFields.map((fieldConfig) => {
                                  const value = (metadata as any)[
                                    fieldConfig.field
                                  ];
                                  if (
                                    value !== undefined &&
                                    value !== null &&
                                    value !== ''
                                  ) {
                                    // Format value: join arrays, convert others to string
                                    const displayValue = Array.isArray(value)
                                      ? value.join(', ')
                                      : String(value);
                                    return (
                                      <p
                                        key={fieldConfig.field}
                                        className="featured-card__meta-item"
                                      >
                                        <span className="featured-card__meta-label">
                                          {fieldConfig.label}:
                                        </span>{' '}
                                        <span className="featured-card__meta-value">
                                          {displayValue}
                                        </span>
                                      </p>
                                    );
                                  }
                                  return null;
                                })
                              ) : (
                                <>
                                  {metadata.standardName && (
                                    <p className="featured-card__meta-item">
                                      <span className="featured-card__meta-label">
                                        Format:
                                      </span>{' '}
                                      <span className="featured-card__meta-value">
                                        {metadata.standardName}
                                      </span>
                                    </p>
                                  )}
                                </>
                              )}
                            </div>

                            <hr className="featured-card__divider" />

                            {/* Action buttons */}
                            <div className="featured-card__actions-row">
                              {(() => {
                                const datasetLink =
                                  (metadata as any).link || '';
                                const format = (metadata as any).format;
                                const canOpenInAgol =
                                  !!agolBaseUrl &&
                                  !!datasetLink &&
                                  isAgolEligibleFormat(
                                    [format],
                                    agolEligibleFormats,
                                  );

                                return canOpenInAgol ? (
                                  <button
                                    className="featured-card__btn featured-card__btn--agol"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(
                                        buildAgolUrl(agolBaseUrl, datasetLink),
                                        '_blank',
                                        'noopener,noreferrer',
                                      );
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faGlobe} /> Open in
                                    AGOL
                                  </button>
                                ) : null;
                              })()}
                              {showChatWithPdfButton ? (
                                <button
                                  className="featured-card__btn featured-card__btn--chat-pdf"
                                  onClick={(e) =>
                                    handleChatWithPdfClick(e, metadata)
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={faCommentDots}
                                    className="featured-card__btn-icon"
                                  />
                                  {chatWithPdfButtonText}
                                  {pdfPageCount && (
                                    <span className="featured-card__pdf-pages">
                                      {pdfPageCount}{' '}
                                      {pdfPageCount === 1 ? 'page' : 'pages'}
                                    </span>
                                  )}
                                </button>
                              ) : null}
                              <button
                                className="featured-card__btn featured-card__btn--dropdown"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Tools{' '}
                                <span className="featured-card__btn-arrow">
                                  ▾
                                </span>
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    },
                  )}
                </div>
              )}

              {/* Table View */}
              {viewType === 'table' && (
                <div className="results-table-view">
                  <table className="results-table">
                    <thead>
                      <tr>
                        <th>Thumbnail</th>
                        <th>Title</th>
                        <th>Format</th>
                        <th>Publish Date</th>
                        <th>Event</th>
                        <th>Acquired</th>
                        <th>Platform</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(currentState.results.data as Metadata[]).map(
                        (metadata: Metadata) => (
                          <tr
                            key={metadata['geonet:info']?.uuid}
                            onMouseEnter={() => setHoveredItem(metadata)}
                            onMouseLeave={() => clearHoveredItem()}
                          >
                            <td className="table-thumbnail">
                              {metadata.thumbnailUrl ? (
                                <img
                                  src={metadata.thumbnailUrl}
                                  alt={metadata.title}
                                  className="table-thumb-img"
                                />
                              ) : (
                                <FontAwesomeIcon icon={iconFaFilePdf} />
                              )}
                            </td>
                            <td>
                              <button
                                className="table-title-link"
                                onClick={() =>
                                  handleMetadataClick(
                                    metadata['geonet:info']?.uuid,
                                  )
                                }
                              >
                                {metadata.title}
                              </button>
                            </td>
                            <td>{metadata.standardName || '-'}</td>
                            <td>{metadata.publicationDate || '-'}</td>
                            <td>{metadata.event || '-'}</td>
                            <td>{metadata.acquiredDate || '-'}</td>
                            <td>{metadata.platform || '-'}</td>
                            <td>
                              <button className="table-action-btn">
                                Tools
                              </button>
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Grid View - Compact thumbnails */}
              {viewType === 'grid' && (
                <div className="results-grid-view">
                  {(currentState.results.data as Metadata[]).map(
                    (metadata: Metadata) => (
                      <div
                        key={metadata['geonet:info']?.uuid}
                        className="grid-item"
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          handleMetadataClick(metadata['geonet:info']?.uuid)
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleMetadataClick(metadata['geonet:info']?.uuid);
                          }
                        }}
                        onMouseEnter={() => setHoveredItem(metadata)}
                        onMouseLeave={() => clearHoveredItem()}
                        title={metadata.title}
                      >
                        <div className="grid-thumbnail">
                          {metadata.thumbnailUrl ? (
                            <img
                              src={metadata.thumbnailUrl}
                              alt={metadata.title}
                              className="grid-thumb-img"
                            />
                          ) : (
                            <div className="grid-placeholder">
                              <FontAwesomeIcon icon={iconFaFilePdf} />
                            </div>
                          )}
                        </div>
                        <div className="grid-title">{metadata.title}</div>
                      </div>
                    ),
                  )}
                </div>
              )}

              {/* Summary View - Text-focused list */}
              {viewType === 'summary' && (
                <div className="results-summary-view">
                  {(currentState.results.data as Metadata[]).map(
                    (metadata: Metadata) => (
                      <div
                        key={metadata['geonet:info']?.uuid}
                        className="summary-item"
                        onMouseEnter={() => setHoveredItem(metadata)}
                        onMouseLeave={() => clearHoveredItem()}
                      >
                        <div className="summary-header">
                          <button
                            className="summary-title"
                            onClick={() =>
                              handleMetadataClick(metadata['geonet:info']?.uuid)
                            }
                          >
                            {metadata.title}
                          </button>
                          <div className="summary-meta">
                            {metadata.platform && (
                              <span className="meta-badge platform">
                                {metadata.platform}
                              </span>
                            )}
                            {metadata.event && (
                              <span className="meta-badge event">
                                {metadata.event}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="summary-details">
                          {metadata.publicationDate && (
                            <span className="detail-item">
                              <strong>Published:</strong>{' '}
                              {metadata.publicationDate}
                            </span>
                          )}
                          {metadata.acquiredDate && (
                            <span className="detail-item">
                              <strong>Acquired:</strong> {metadata.acquiredDate}
                            </span>
                          )}
                        </div>
                        {metadata.abstract && (
                          <p className="summary-abstract">
                            {metadata.abstract.length > 200
                              ? `${metadata.abstract.substring(0, 200)}...`
                              : metadata.abstract}
                          </p>
                        )}
                      </div>
                    ),
                  )}
                </div>
              )}
            </>
          )}

          {hasResults && (
            <div className="d-flex flex-row-reverse">
              <PaginationComponent
                resultCount={currentState.results.count || 0}
                pageSize={currentState.query?.pageSize || 25}
                pageIndex={currentState.query?.pageIndex || 0}
                pageSizeOptions={[25, 50, 75, 100]}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ResultList;
